/** `GET /practice/next`: the phrases a practice session would cover. */
import { apiFetch, errorMessageFrom } from './client';

export type PracticeReason = 'struggled' | 'review';

export interface PracticePhrase {
  form_id: string;
  text: string;
  locale: string;
  reason: PracticeReason;
}

export interface PracticeNextResponse {
  phrases: PracticePhrase[];
}

/** What a finished practice run reports in `assessment`. */
export interface PracticeSummary {
  phrases_practiced: number;
  first_try: number;
}

/** The phrases due for practice; an empty list means there is nothing to practise. */
export async function fetchPracticePhrases(
  signal?: AbortSignal
): Promise<PracticePhrase[]> {
  const response = await apiFetch('/practice/next', { signal });
  if (!response.ok) {
    throw new Error(await errorMessageFrom(response));
  }
  const data: PracticeNextResponse = await response.json();
  return data.phrases ?? [];
}

/** Reads the counts from a completed run's `assessment`; null when absent or malformed. */
export function toPracticeSummary(
  assessment: Record<string, unknown> | undefined
): PracticeSummary | null {
  if (!assessment) return null;
  const { phrases_practiced, first_try } = assessment;
  if (typeof phrases_practiced !== 'number' || typeof first_try !== 'number') {
    return null;
  }
  return { phrases_practiced, first_try };
}
